import type { ContentBlock, DocPage, NavCategory } from "./types";
import type { ManualLocale } from "../i18n/locale";

function translate(value: string, dict: Record<string, string> | null): string {
  if (!dict) return value;
  return dict[value] || value;
}

function localizeBlock(block: ContentBlock, dict: Record<string, string> | null): ContentBlock {
  switch (block.type) {
    case "paragraph":
    case "heading":
      return { ...block, text: translate(block.text, dict) };
    case "list":
    case "steps":
      return { ...block, items: block.items.map((item) => translate(item, dict)) };
    case "table":
      return {
        ...block,
        headers: block.headers.map((header) => translate(header, dict)),
        rows: block.rows.map((row) => row.map((cell) => translate(cell, dict))),
      };
    case "callout":
      return {
        ...block,
        title: block.title ? translate(block.title, dict) : block.title,
        text: translate(block.text, dict),
      };
    case "code":
      return {
        ...block,
        caption: block.caption ? translate(block.caption, dict) : block.caption,
      };
    case "definitions":
      return {
        ...block,
        items: block.items.map((item) => ({
          term: translate(item.term, dict),
          definition: translate(item.definition, dict),
        })),
      };
    case "figure":
      return {
        ...block,
        alt: translate(block.alt, dict),
        caption: translate(block.caption, dict),
        placeholderNote: block.placeholderNote
          ? translate(block.placeholderNote, dict)
          : block.placeholderNote,
      };
    default:
      return block;
  }
}

export function localizePage(page: DocPage, dict: Record<string, string> | null): DocPage {
  if (!dict) return page;
  return {
    ...page,
    title: translate(page.title, dict),
    description: translate(page.description, dict),
    category: translate(page.category, dict),
    sections: page.sections.map((section) => ({
      ...section,
      title: translate(section.title, dict),
    })),
    blocks: page.blocks.map((block) => localizeBlock(block, dict)),
  };
}

export function localizeNavigation(
  navigation: NavCategory[],
  pages: DocPage[],
  dict: Record<string, string> | null
): NavCategory[] {
  const titles = new Map(pages.map((page) => [page.id, page.title]));
  return navigation.map((category) => ({
    ...category,
    title: translate(category.title, dict),
    pages: category.pages.map((page) => ({
      ...page,
      title: titles.get(page.id) || translate(page.title, dict),
    })),
  }));
}

export function dictionaryFor(locale: ManualLocale, maps: {
  de: Record<string, string>;
  fr: Record<string, string>;
}) {
  if (locale === "de") return maps.de;
  if (locale === "fr") return maps.fr;
  return null;
}
